import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { CtaBand } from '../components/ui/CtaBand';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Eyebrow } from '../components/ui/Eyebrow';
import { carePaths, getCarePath } from '../data/carePaths';
import { useBrand } from '../contexts/BrandContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
export function CarePathDetail() {
  const {
    slug = ''
  } = useParams();
  const path = getCarePath(slug);
  const {
    brand,
    carePathImage
  } = useBrand();
  useDocumentMeta({
    title: path ? `${path.name} — ${brand.name}` : `Care path not found — ${brand.name}`,
    description: path?.summary ?? 'This care path could not be found.'
  });
  if (!path) {
    return <main className="w-full bg-cream" data-id="element-461" data-mp="2jkfrdsc3l0qg">
        <div className="mx-auto max-w-shell px-6 py-32 md:px-8" data-id="element-462" data-mp="2jl7kmwi82mdk">
          <h1 className="font-serif text-4xl text-ink" data-id="element-463" data-mp="6n3xi58i19j7">We couldn’t find that care path.</h1>
          <p className="mt-5 max-w-lg leading-relaxed text-ink-soft" data-id="element-464" data-mp="1oizmcgpz1xx">
            That link may be out of date — our fault, not yours. Everything we offer is on the care
            paths page.
          </p>
          <Button to="/care" className="mt-8" data-id="element-465" data-mp="3otid08mf1rjp">
            See all care paths
          </Button>
        </div>
      </main>;
  }
  const related = carePaths.filter(item => item.slug !== path.slug).slice(0, 3);
  return <main className="w-full bg-cream" data-id="element-466" data-mp="ldba44btrybz">
      <div className="mx-auto max-w-shell px-6 pt-10 md:px-8 md:pt-14" data-id="element-467" data-mp="lecu4yatjv5u">
        <Link to="/care" className="inline-flex items-center gap-2 text-sm text-ink-soft transition-colors duration-150 ease-brand hover:text-brick" data-id="element-468" data-mp="gjywsqcljdyj">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-469" data-mp="192w4djdfii0k" />
          All care paths
        </Link>
      </div>

      <header className="bg-cream" data-id="element-470" data-mp="149ji3fw82naz">
        <div className="mx-auto grid max-w-shell gap-12 px-6 py-12 md:px-8 md:py-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20" data-id="element-471" data-mp="1499svuj04k6c">
          <div data-id="element-472" data-mp="1s6z9y1a9zghl">
            <Eyebrow className="text-brick" data-id="element-473" data-mp="1s5ehch7iu7md">{path.category}</Eyebrow>
            <h1 className="mt-5 font-serif text-[2.75rem] leading-[1.02] text-ink md:text-[4rem]" data-id="element-474" data-mp="1efay7z3xuu15">
              {path.name}
            </h1>
            <p className="mt-6 max-w-xl font-serif text-2xl italic leading-snug text-brick" data-id="element-475" data-mp="1ifbnkeskspmz">
              {path.summary}
            </p>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-soft" data-id="element-476" data-mp="14j7yezwk77hd">
              {path.description}
            </p>

            <div className="rule mt-10 pt-8" data-id="element-477" data-mp="vgovygwoqwvn">
              <p className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-478" data-mp="vhzzcfm7y2m8">
                This is for you if
              </p>
              <ul className="mt-5 flex flex-col gap-3" data-id="element-479" data-mp="2puhk9gjl4qnp">
                {path.forYouIf.map(line => <li key={line} className="flex items-start gap-3" data-id="element-480" data-mp="2twbgncmqz8i2">
                    <CheckIcon className="mt-1.5 h-4 w-4 shrink-0 text-brick" strokeWidth={2} aria-hidden="true" data-id="element-481" data-mp="2ttgu9fk6bdm0" />
                    <span className="leading-relaxed text-ink" data-id="element-482" data-mp="e51ktux3prkf">{line}</span>
                  </li>)}
              </ul>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4" data-id="element-483" data-mp="n6hsvw0g1hlh">
              <Button to="/start" data-id="element-484" data-mp="11vxjnbzz5n16">
                Start with one question
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-485" data-mp="11y9w59myr56u" />
              </Button>
              <Button to="/symptoms" variant="secondary" data-id="element-486" data-mp="wzowbbbminix">
                Not sure yet? Use the decoder
              </Button>
            </div>
          </div>

          <div data-id="element-487" data-mp="15zbyw0g39zdk">
            <img src={carePathImage(path.slug, path.image)} alt="" className="h-72 w-full rounded-brand object-cover md:h-96" data-id="element-488" data-mp="15ytuggj9weuk" />
            <dl className="mt-8 grid gap-px overflow-hidden rounded-brand border border-ink/15 bg-ink/15 sm:grid-cols-2" data-id="element-489" data-mp="37qp1ujyhxx6z">
              <div className="bg-cream-light p-6" data-id="element-490" data-mp="3a651rv3tmamk">
                <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-491" data-mp="3e4w36efbkefh">From</dt>
                <dd className="mt-2 font-serif text-3xl text-ink" data-id="element-492" data-mp="3j4h0kkeuceq3">
                  ${path.priceFrom}
                  <span className="text-base text-ink-muted" data-id="element-493" data-mp="3o43fqze8c51j"> {path.cadence}</span>
                </dd>
              </div>
              <div className="bg-cream-light p-6" data-id="element-494" data-mp="2qhnx3ua2h1ce">
                <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-495" data-mp="2vewgtzav5dfr">
                  Price held
                </dt>
                <dd className="mt-2 font-serif text-3xl text-ink" data-id="element-496" data-mp="30dh7p8486krj">12 months</dd>
              </div>
              <div className="bg-cream-light p-6 sm:col-span-2" data-id="element-497" data-mp="355r2p2k76b0o">
                <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-498" data-mp="27q3hrtyy2pgx">
                  Before we prescribe
                </dt>
                <dd className="mt-2 leading-relaxed text-ink-soft" data-id="element-499" data-mp="2cmtwodz0jf0r">{path.eligibility}</dd>
              </div>
            </dl>
          </div>
        </div>
      </header>

      <section className="bg-cream-light" data-id="element-500" data-mp="2073z5qjb51ym">
        <div className="mx-auto grid max-w-shell gap-14 px-6 py-20 md:px-8 md:py-24 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20" data-id="element-501" data-mp="203zm03nv6vvt">
          <div data-id="element-502" data-mp="1q8508zocgkot">
            <Eyebrow className="text-brick" data-id="element-503" data-mp="1q8eig135dnjy">What’s included</Eyebrow>
            <ul className="mt-8" data-id="element-504" data-mp="1qa94xju2hnq5">
              {path.includes.map(item => <li key={item} className="rule flex items-start gap-4 py-4" data-id="element-505" data-mp="1m8e1o9skt09q">
                  <CheckIcon className="mt-1.5 h-4 w-4 shrink-0 text-brick" strokeWidth={2} aria-hidden="true" data-id="element-506" data-mp="1he02dwasm8zs" />
                  <span className="leading-relaxed text-ink" data-id="element-507" data-mp="17ipz758nrvkf">{item}</span>
                </li>)}
            </ul>
          </div>

          <div data-id="element-508" data-mp="12k4xf1drd3tv">
            <Eyebrow className="text-brick" data-id="element-509" data-mp="ygyvu71lc0tq">How the care runs</Eyebrow>
            <ol className="mt-8" data-id="element-510" data-mp="yitgrlcv3sxt">
              {path.protocol.map((step, index) => <li key={step.title} className="rule py-6" data-id="element-511" data-mp="nuacbua366wi" data-mpi={index}>
                  <div className="flex gap-6" data-id="element-512" data-mp="suyar10n9q0r">
                    <span className="font-serif text-xl text-brick" aria-hidden="true" data-id="element-513" data-mp="sslls3wgyfe7">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div data-id="element-514" data-mp="jujp20jrq8wq">
                      <h3 className="font-serif text-xl leading-snug text-ink" data-id="element-515" data-mp="jw4an0o3uhig">{step.title}</h3>
                      <p className="mt-3 max-w-2xl leading-relaxed text-ink-soft" data-id="element-516" data-mp="17lp267ybqn3c">{step.detail}</p>
                    </div>
                  </div>
                </li>)}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-cream" data-id="element-517" data-mp="12s2p4eo90h70">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-518" data-mp="12rb1bq0w7nra">
          <SectionHeading eyebrow="What may be prescribed" title="Options, with their real regulatory status." intro="Nothing here is prescribed automatically. What you are offered depends on your history, your contraindications and what you actually want." data-id="element-519" data-mp="1qhf6w2xn5xkx" />
          <ul className="mt-12" data-id="element-520" data-mp="20lluf22lsvfv">
            {path.options.map(option => <li key={option.name} className="rule" data-id="element-521" data-mp="2ee1q1ht6dr6r">
                <div className="grid gap-3 py-6 md:grid-cols-[1fr_1fr_1.4fr] md:gap-10" data-id="element-522" data-mp="2efwfn9fe67h6">
                  <p className="font-serif text-xl text-ink" data-id="element-523" data-mp="29gs4wectk1oq">{option.name}</p>
                  <p className="text-ink-soft" data-id="element-524" data-mp="1lpw8f3xf4ib5">{option.form}</p>
                  <p className="text-ink-soft" data-id="element-525" data-mp="1ho13lpga3msm">{option.note}</p>
                </div>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-526" data-mp="1vl7mdk105lhu">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-527" data-mp="1qnxzoc4zznxp">
          <SectionHeading eyebrow="Other paths" title="You may be in more than one of these." data-id="element-528" data-mp="372h2pxou3xex" />
          <ul className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-529" data-mp="323cwnfx7ifst">
            {related.map(item => <li key={item.slug} className="flex" data-id="element-530" data-mp="10egoc44j34u1">
                <Link to={`/care/${item.slug}`} className="group flex w-full flex-col rounded-brand border border-ink/15 bg-cream p-7" data-id="element-531" data-mp="10g1n65xvl9xp">
                  <p className="text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-532" data-mp="19dbytselv135">
                    {item.category}
                  </p>
                  <h3 className="mt-3 font-serif text-2xl leading-snug text-ink transition-colors duration-150 ease-brand group-hover:text-brick" data-id="element-533" data-mp="1j4on6a17394f">
                    {item.name}
                  </h3>
                  <p className="mt-3 text-[0.9375rem] leading-relaxed text-ink-soft" data-id="element-534" data-mp="1s5mkk9h4oie1">
                    {item.summary}
                  </p>
                  <p className="rule mt-auto pt-5 text-sm text-ink-soft" data-id="element-535" data-mp="1x36bo9yxcpne">
                    From ${item.priceFrom} {item.cadence}
                  </p>
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-536" data-mp="3rcbhv95hpjbl" />
    </main>;
}